import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { type RootState } from '@/store';
import { ToolkitGroup, ToolkitGroupCollection, ToolkitTool } from '@/api/toolkit';
import { normalizeToolkitSearchQuery, toolkitToolMatchesSearch } from '@/utils/toolkitSearch';

interface ToolkitState {
  toolkitGroupCollections: ToolkitGroupCollection[];
  userTools: ToolkitTool[];
}

interface SetToolkitDataPayload {
  toolkitGroupCollections: ToolkitGroupCollection[];
  userTools: ToolkitTool[];
}

const initialState: ToolkitState = {
  toolkitGroupCollections: [],
  userTools: [],
};

const toolkitSlice = createSlice({
  name: 'toolkit',
  initialState: initialState,
  reducers: {
    setToolkitData: (state, action: PayloadAction<SetToolkitDataPayload>) => {
      state.toolkitGroupCollections = action.payload.toolkitGroupCollections;
      state.userTools = action.payload.userTools;
    },
  },
});

export const { setToolkitData } = toolkitSlice.actions;

export const selectToolkitGroupCollections = (state: RootState) => state.toolkit.toolkitGroupCollections;

const filterGroupsByQuery = (groups: ToolkitGroup[], normalizedQuery: string): ToolkitGroup[] => {
  if (!normalizedQuery) {
    return groups;
  }

  return groups.reduce<ToolkitGroup[]>((filteredGroups, group) => {
    const tools = group.tools.filter((tool) => toolkitToolMatchesSearch(tool, normalizedQuery));

    if (tools.length === 0) {
      return filteredGroups;
    }

    filteredGroups.push({
      ...group,
      tools,
    });

    return filteredGroups;
  }, []);
};

export const makeSelectFilteredToolkitGroups = () =>
  createSelector(
    [
      selectToolkitGroupCollections,
      (_: RootState, query: string) => query,
      (_: RootState, _query: string, activeCollectionTitle: string | null) => activeCollectionTitle,
    ],
    (collections, query, activeCollectionTitle) => {
      const normalizedQuery = normalizeToolkitSearchQuery(query);

      const sourceGroups = collections.find((c) => c.collectionTitle === activeCollectionTitle)?.toolGroups ?? [];

      return filterGroupsByQuery(sourceGroups, normalizedQuery);
    },
  );

export type CollectionMatchCounts = Record<string, number>;

export const makeSelectCollectionMatchCounts = () =>
  createSelector(
    [selectToolkitGroupCollections, (_: RootState, query: string) => query],
    (collections, query): CollectionMatchCounts => {
      const normalizedQuery = normalizeToolkitSearchQuery(query);

      // No counts unless the user is actively searching - badges only make sense
      // when there are search results to surface across collections.
      if (!normalizedQuery) {
        return {};
      }

      return collections.reduce<CollectionMatchCounts>((acc, collection) => {
        const count = collection.toolGroups.reduce(
          (sum, group) => sum + group.tools.filter((tool) => toolkitToolMatchesSearch(tool, normalizedQuery)).length,
          0,
        );
        acc[collection.collectionTitle] = count;
        return acc;
      }, {});
    },
  );

export const toolkitReducer = toolkitSlice.reducer;
